import { AdminAccessNotice } from "@/components/admin/access-notice";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, useCallback, useRef } from "react";
import { getCsrfToken } from "@/aws/client";
import { operation } from "@/lib/api-client";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, ShieldOff, Save, History, RotateCcw, Eye } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/constitution")({
  component: AdminConstitutionPage,
});

interface PromptVersion {
  id: string;
  content?: string;
  content_length?: number;
  is_active: boolean;
  note: string | null;
  created_by: string;
  created_at: string;
}

function AdminConstitutionPage() {
  const { isAdmin, loading: roleLoading, error: accessError, retry: retryAccess } = useIsAdmin();

  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string|null>(null);
  const saveLock=useRef(false);
  const [loadingVersion, setLoadingVersion] = useState<string | null>(null);
  const versionRequest = useRef(0);
  const refreshRequest = useRef(0);
  const request=useRef<{signature:string;id:string}|null>(null);
  const [content, setContent] = useState("");
  const [note, setNote] = useState("");
  const [dirty, setDirty] = useState(false);
  const [previewVersion, setPreviewVersion] = useState<PromptVersion | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const refresh = useCallback(async () => {
    const generation = ++refreshRequest.current;
    setLoading(true);
    setLoadError(null);
    try {
      type Cursor = {createdAt:string;id:string};
      const rows:PromptVersion[]=[];
      let after:Cursor | undefined;
      do {
        const page=await operation<{after?:Cursor},{versions:PromptVersion[];next:Cursor|null}>("constitution_list",after?{after}:{},getCsrfToken());
        if(generation!==refreshRequest.current)return;
        rows.push(...page.versions); after=page.next??undefined;
      } while(after);
      const active=await operation<{},PromptVersion|null>("constitution_get",{},getCsrfToken());
      if(generation!==refreshRequest.current)return;
      setVersions(active ? [active,...rows.filter(row=>row.id!==active.id)].map(row=>({...row,is_active:row.id===active.id})) : rows.map(row=>({...row,is_active:false})));
      setContent(active?.content??"");
      setDirty(false);
    } catch(error) {if(generation===refreshRequest.current)setLoadError(error instanceof Error ? error.message : "Could not load prompt versions.");}
    finally {if(generation===refreshRequest.current)setLoading(false);}
  }, []);

  useEffect(() => {
    if (isAdmin) refresh();
    return () => {refreshRequest.current++;versionRequest.current++;};
  }, [isAdmin, refresh]);

  const save = async () => {
    if(!isAdmin || saveLock.current || loading || loadError)return;
    if(!content.trim()){toast.error("Prompt content cannot be empty.");return;}
    saveLock.current=true;setSaving(true);
    try {
      const data={content:content.trim(),note:note.trim()||null};
      const signature=JSON.stringify(data);
      const clientRequestId=request.current?.signature===signature?request.current.id:crypto.randomUUID();
      request.current={signature,id:clientRequestId};
      await operation("constitution_save",{...data,clientRequestId},getCsrfToken());
      request.current=null;setNote("");setDirty(false);
      toast.success("New version saved & activated.");await refresh();
    } catch(error) {toast.error(error instanceof Error ? error.message : "Could not save.");}
    finally {saveLock.current=false;setSaving(false);}
  };
  const activateVersion = async (version:PromptVersion) => {
    if(!isAdmin || saveLock.current)return;
    saveLock.current=true;setSaving(true);
    try {await operation("constitution_activate",{id:version.id},getCsrfToken());toast.success("Version activated.");await refresh();}
    catch(error){toast.error(error instanceof Error ? error.message : "Could not activate version.");}
    finally {saveLock.current=false;setSaving(false);}
  };

  const openVersion = async (version:PromptVersion, restore:boolean) => {
    const request=++versionRequest.current;
    setLoadingVersion(version.id);
    try {
      const loaded=await operation<{id:string},PromptVersion>("constitution_get",{id:version.id},getCsrfToken());
      if(request!==versionRequest.current)return;
      if(restore){setContent(loaded.content??"");setDirty(true);setShowHistory(false);toast.info("Version loaded into editor. Save to activate.");}
      else setPreviewVersion(loaded);
    } catch(error){if(request===versionRequest.current)toast.error(error instanceof Error ? error.message : "Could not load this version.");}
    finally {if(request===versionRequest.current)setLoadingVersion(null);}
  };

  if (roleLoading) {
    return (
      <AppLayout>
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  if (accessError && !roleLoading) return <AppLayout><div className="p-6"><AdminAccessNotice error={accessError} retry={retryAccess} /></div></AppLayout>;

  if (!isAdmin) {
    return (
      <AppLayout>
        <div className="flex h-full items-center justify-center px-6">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <ShieldOff className="h-10 w-10 mx-auto text-muted-foreground" />
              <h1 className="mt-4 text-ui-xl font-bold text-foreground">Admins only</h1>
              <p className="mt-2 text-ui-base text-muted-foreground">
                You need the admin role to edit the system prompt.
              </p>
              <Button asChild variant="outline" className="mt-6">
                <Link to="/chat">Back to chat</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const activeVersion = versions.find((v) => v.is_active);

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-6 py-8">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-ui-2xl font-bold text-foreground">System Prompt</h1>
              <p className="text-ui-base text-muted-foreground">
                Edit the default AskBell constitution. Changes apply to all new messages immediately.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" disabled={loading || saving || Boolean(loadError)} onClick={() => setShowHistory(true)}>
                <History className="h-4 w-4" />
                History ({versions.length})
              </Button>
              <Button onClick={save} disabled={saving || loading || Boolean(loadError) || !dirty}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save new version
              </Button>
            </div>
          </div>

          {loadError ? <div role="alert" className="rounded-ui-md border border-destructive/30 p-4 text-ui-base"><p>{loadError}</p><Button variant="outline" className="mt-3" onClick={()=>void refresh()}>Try again</Button></div> : loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              {activeVersion && (
                <p className="text-ui-sm text-muted-foreground">
                  Active version from{" "}
                  {new Date(activeVersion.created_at).toLocaleString()}
                  {activeVersion.note ? ` — "${activeVersion.note}"` : ""}
                </p>
              )}

              <Textarea
                disabled={saving}
                value={content}
                onChange={(e) => {
                  setContent(e.target.value);
                  setDirty(true);
                }}
                className="min-h-[500px] font-mono text-ui-sm leading-relaxed"
                placeholder="Paste the full AskBell behavior system prompt here…"
              />

              <div className="flex items-end gap-3">
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor="change-note">Change note (optional)</Label>
                  <Input
                    disabled={saving}
                    id="change-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Added guidance for referring users to Research department"
                    maxLength={200}
                  />
                </div>
              </div>

              <p className="text-ui-xs text-muted-foreground">
                {content.length.toLocaleString()} characters ·{" "}
                <code className="text-ui-xs">{"{{currentDateTime}}"}</code> is replaced at runtime
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Version History Dialog */}
      <Dialog open={showHistory} onOpenChange={(open)=>{setShowHistory(open);if(!open){versionRequest.current++;setLoadingVersion(null);}}}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 py-2">
            {versions.length === 0 ? (
              <p className="text-ui-base text-muted-foreground text-center py-8">
                No versions yet. Save your first version above.
              </p>
            ) : (
              versions.map((v) => (
                <Card key={v.id} className={v.is_active ? "border-primary/50" : ""}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-ui-sm font-bold text-foreground">
                          {new Date(v.created_at).toLocaleString()}
                        </p>
                        {v.is_active && (
                          <span className="text-ui-xs font-bold uppercase tracking-wider text-primary">
                            Active
                          </span>
                        )}
                      </div>
                      {v.note && (
                        <p className="text-ui-sm text-muted-foreground mt-0.5 truncate">
                          {v.note}
                        </p>
                      )}
                      <p className="text-ui-xs text-muted-foreground mt-0.5">
                        {(v.content_length??v.content?.length??0).toLocaleString()} chars
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Preview"
                        disabled={Boolean(loadingVersion)}
                        onClick={() => openVersion(v,false)}
                      >
                        {loadingVersion===v.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      {!v.is_active && (
                        <Button
                          size="sm"
                          variant="outline"
                          title="Restore this version"
                          disabled={Boolean(loadingVersion)}
                          onClick={() => openVersion(v,true)}
                        >
                          <RotateCcw className="h-3 w-3" />
                          Restore
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewVersion} onOpenChange={(o) => !o && setPreviewVersion(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Version from {previewVersion && new Date(previewVersion.created_at).toLocaleString()}
              {previewVersion?.note ? ` — ${previewVersion.note}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            <pre className="text-ui-sm font-mono whitespace-pre-wrap text-muted-foreground leading-relaxed p-4 bg-muted/30 rounded-ui-sm">
              {previewVersion?.content}
            </pre>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
